<template>
  <div class="p-3 md:p-6 bg-gray-50 dark:bg-gray-900 min-h-screen font-sans">
    <!-- Header -->
    <div class="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4 bg-white dark:bg-gray-800 p-4 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700">
      <div class="flex items-center space-x-3">
        <div class="h-10 w-1.5 bg-gradient-to-b from-cyan-500 to-blue-600 rounded-full"></div>
        <div>
          <h1 class="text-xl md:text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <Icon icon="mdi:door-open" class="text-cyan-600 dark:text-cyan-400" />
            Control de Acceso en Vivo
          </h1>
          <p class="text-xs text-gray-500 dark:text-gray-400 mt-1 flex items-center">
            <span class="w-2 h-2 bg-green-500 rounded-full animate-pulse mr-2"></span>
            {{ escuchaActiva ? 'Escuchando lector FS88H · Acceso automático' : 'Iniciando escucha de huella...' }}
          </p>
        </div>
      </div>
      <div class="flex gap-2 flex-wrap">
        <!-- Botón para configurar agente local -->
        <button
          @click="showConfigAgente = true"
          class="px-3 py-2.5 rounded-xl font-bold transition-all flex items-center gap-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600"
          title="Configurar Agente Local (Gym Laptop)"
        >
          <Icon icon="mdi:cog" class="text-lg text-cyan-600 dark:text-cyan-400" />
          <span class="hidden sm:inline">Agente Local</span>
        </button>

        <!-- Botón para desbloquear audio (solo visible si no está desbloqueado) -->
        <button 
          v-if="!sonidoDesbloqueado"
          @click="desbloquearAudioManual" 
          class="px-3 py-2.5 rounded-xl font-bold transition-all flex items-center gap-2 text-sm bg-amber-500 hover:bg-amber-600 text-white animate-pulse shadow-lg"
        >
          <Icon icon="mdi:volume-high" class="text-lg" />
          <span>🔊 Activar Sonido</span>
        </button>

        <button @click="toggleSonido" :class="[
          'px-3 py-2.5 rounded-xl font-bold transition-all flex items-center gap-2 text-sm border-2',
          sonidoActivo
            ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800 text-emerald-600 dark:text-emerald-400'
            : 'bg-gray-100 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-500'
        ]">
          <Icon :icon="sonidoActivo ? 'mdi:volume-high' : 'mdi:volume-off'" class="text-lg" />
          <span class="hidden sm:inline">{{ sonidoActivo ? 'Sonido ON' : 'Sonido OFF' }}</span>
        </button>

        <button @click="cargar" class="bg-cyan-600 hover:bg-cyan-700 text-white px-4 py-2.5 rounded-xl font-bold transition-all shadow-md flex items-center gap-2 text-sm">
          <Icon icon="mdi:refresh" :class="{'animate-spin': cargando}" />
          Actualizar
        </button>
      </div>
    </div>

    <!-- Indicador de escucha continua de huella -->
    <div v-if="escuchaActiva" class="mb-4 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-xl p-4 shadow-lg flex items-center gap-4">
      <div class="p-3 bg-white/20 rounded-xl animate-pulse">
        <Icon icon="mdi:fingerprint" class="text-3xl" />
      </div>
      <div class="flex-1">
        <p class="font-bold text-lg">🎧 ESCUCHANDO LECTOR BIOMÉTRICO (FS88H)</p>
        <p class="text-xs opacity-90">Coloque el dedo en el lector para acceso por huella, o ingrese la cédula abajo.</p>
      </div>
      <div class="text-right">
        <p class="text-[10px] uppercase opacity-75">Huellas procesadas</p>
        <p class="text-2xl font-black">{{ huellasProcessadas }}</p>
      </div>
    </div>

    <!-- PANEL DE HARDWARE -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
      <!-- TORNIQUETE -->
      <div class="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
        <div class="flex items-center justify-between mb-3">
          <div class="flex items-center gap-3">
            <div :class="['w-3 h-3 rounded-full', torniqueteStatus.conectado ? 'bg-green-500 animate-pulse' : 'bg-red-500']"></div>
            <div>
              <p class="text-sm font-bold text-gray-800 dark:text-white flex items-center gap-2">
                <Icon icon="mdi:gate" class="text-emerald-500" />
                Torniquete
              </p>
              <p class="text-[10px] text-gray-500 font-mono">
                Puerto: {{ torniqueteStatus.puerto || 'COM4' }} · {{ torniqueteStatus.conectado ? 'CONECTADO' : 'DESCONECTADO' }}
              </p>
            </div>
          </div>
          <button @click="verificarTorniquete" class="p-2 text-gray-400 hover:text-cyan-600 rounded-lg" title="Verificar estado">
            <Icon icon="mdi:refresh" :class="{'animate-spin': verificandoTorniquete}" />
          </button>
        </div>
        <button @click="confirmarAbrirTorniquete" :disabled="abriendoTorniquete"
          class="w-full py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl font-bold text-sm shadow-md flex items-center justify-center gap-2 disabled:opacity-50 hover:brightness-105 active:scale-98 transition-all">
          <Icon :icon="abriendoTorniquete ? 'mdi:loading' : 'mdi:lock-open-variant'" :class="{'animate-spin': abriendoTorniquete}" class="text-lg" />
          {{ abriendoTorniquete ? 'Abriendo torniquete...' : 'Abrir Torniquete Manual' }}
        </button>
      </div>

      <!-- BIOMÉTRICO FS88H -->
      <div class="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
        <div class="flex items-center justify-between mb-3">
          <div class="flex items-center gap-3">
            <div :class="['w-3 h-3 rounded-full', biometricoStatus.conectado ? 'bg-green-500 animate-pulse' : 'bg-red-500']"></div>
            <div>
              <p class="text-sm font-bold text-gray-800 dark:text-white flex items-center gap-2">
                <Icon icon="mdi:fingerprint" class="text-purple-500" />
                Lector FS88H
              </p>
              <p class="text-[10px] text-gray-500 font-mono">
                {{ escuchaActiva ? '🎧 ESCUCHANDO' : 'En pausa' }} · {{ biometricoStatus.conectado ? 'CONECTADO' : 'DESCONECTADO' }}
              </p>
            </div>
          </div>
          <button @click="verificarBiometrico" class="p-2 text-gray-400 hover:text-purple-600 rounded-lg" title="Verificar estado">
            <Icon icon="mdi:refresh" :class="{'animate-spin': verificandoBiometrico}" />
          </button>
        </div>
        <div class="w-full py-2.5 bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 border-2 border-purple-300 dark:border-purple-700 text-purple-600 dark:text-purple-400 rounded-xl font-bold text-sm flex items-center justify-center gap-2">
          <Icon :icon="escuchaActiva ? 'mdi:ear-hearing' : 'mdi:ear-hearing-off'" :class="{'animate-pulse': escuchaActiva}" class="text-lg" />
          {{ escuchaActiva ? 'Escuchando automáticamente' : 'Escucha en pausa' }}
        </div>
      </div>

      <!-- FSO701 FACIAL -->
      <div class="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-3">
            <div :class="['w-3 h-3 rounded-full', fso701Status.conectado ? 'bg-green-500 animate-pulse' : 'bg-red-500']"></div>
            <div>
              <p class="text-sm font-bold text-gray-800 dark:text-white flex items-center gap-2">
                <Icon icon="mdi:face-recognition" class="text-cyan-500" />
                FSO701 (Facial)
              </p>
              <p class="text-[10px] text-gray-500 font-mono">
                IP: 192.168.9.132 · {{ fso701Status.conectado ? 'CONECTADO' : 'DESCONECTADO' }}
              </p>
            </div>
          </div>
        </div>
        <div class="mt-3 text-right">
          <p class="text-[10px] text-gray-400 uppercase">Último acceso</p>
          <p class="text-sm font-black text-cyan-600">{{ ultimoAcceso }}</p>
        </div>
      </div>
    </div>

    <!-- 🔥 SECCIÓN DE ACCESO RÁPIDO POR CÉDULA / SCANNER (ABRE EL TORNIQUETE) -->
    <div class="bg-gradient-to-r from-cyan-600 via-blue-600 to-indigo-700 rounded-2xl p-4 sm:p-5 shadow-lg mb-6 text-white border border-cyan-400/30">
      <div class="flex flex-col lg:flex-row items-center justify-between gap-4">
        <div class="flex items-center gap-3 w-full lg:w-auto">
          <div class="p-3 bg-white/20 backdrop-blur-md rounded-2xl flex-shrink-0">
            <Icon icon="mdi:card-account-details-outline" class="text-3xl sm:text-4xl text-white" />
          </div>
          <div>
            <h2 class="text-lg sm:text-xl font-black tracking-tight flex items-center gap-2">
              Acceso por Cédula / Documento
              <span class="text-[10px] uppercase font-bold tracking-wider px-2.5 py-0.5 bg-emerald-400 text-emerald-950 rounded-full">
                Abre Torniquete COM
              </span>
            </h2>
            <p class="text-xs text-cyan-100 mt-0.5">
              Escriba o escanee la cédula del socio (presione Enter o botón para validar y abrir el torniquete).
            </p>
          </div>
        </div>

        <form @submit.prevent="validarCedulaManual" class="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full lg:w-auto">
          <div class="relative flex-1 sm:w-80">
            <div class="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400">
              <Icon icon="mdi:barcode-scan" class="text-xl" />
            </div>
            <input
              ref="inputCedulaRef"
              v-model="cedulaInput"
              type="text"
              placeholder="Ej: V-12345678 o 12345678"
              autocomplete="off"
              :disabled="procesandoCedula"
              class="w-full pl-11 pr-10 py-3 bg-white text-gray-900 placeholder-gray-400 font-bold text-sm rounded-xl border-2 border-transparent focus:border-cyan-300 focus:outline-none focus:ring-4 focus:ring-cyan-300/30 shadow-inner"
            />
            <button
              v-if="cedulaInput"
              type="button"
              @click="cedulaInput = ''; $refs.inputCedulaRef?.focus()"
              class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
            >
              <Icon icon="mdi:close-circle" class="text-lg" />
            </button>
          </div>

          <button
            type="submit"
            :disabled="!cedulaInput.trim() || procesandoCedula"
            class="px-5 py-3 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-gray-950 font-black text-sm rounded-xl shadow-md transition-all flex items-center justify-center gap-2 whitespace-nowrap active:scale-95 cursor-pointer"
          >
            <Icon :icon="procesandoCedula ? 'mdi:loading' : 'mdi:door-open'" :class="{'animate-spin': procesandoCedula}" class="text-lg" />
            <span>{{ procesandoCedula ? 'Validando...' : 'Dar Acceso' }}</span>
          </button>
        </form>
      </div>
    </div>

    <!-- MODAL DE VERIFICACIÓN -->
    <Teleport to="body">
      <div v-if="mostrarVerificando" class="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-[9998]">
        <div class="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl w-full max-w-md overflow-hidden animate-zoomIn border-4 border-cyan-500">
          <div class="p-8 text-center">
            <div class="relative mx-auto w-32 h-32 mb-6">
              <div class="absolute inset-0 bg-cyan-500/20 rounded-full animate-ping"></div>
              <div class="relative bg-gradient-to-br from-cyan-500 to-blue-600 rounded-full w-32 h-32 flex items-center justify-center shadow-xl">
                <Icon :icon="metodoVerificando === 'cedula' ? 'mdi:card-account-details' : 'mdi:fingerprint'" class="text-white text-7xl" />
              </div>
              <div class="absolute -inset-2 rounded-full border-4 border-cyan-300 border-t-cyan-600 animate-spin"></div>
            </div>

            <h2 class="text-2xl font-black text-gray-800 dark:text-white mb-2">
              Verificando...
            </h2>
            <p class="text-sm text-gray-500 dark:text-gray-400">
              {{ metodoVerificando === 'cedula' ? 'Validando cédula y membresía del socio' : 'Identificando huella y validando membresía' }}
            </p>

            <div class="flex justify-center gap-2 mt-6">
              <div class="w-3 h-3 bg-cyan-500 rounded-full animate-bounce" style="animation-delay: 0s"></div>
              <div class="w-3 h-3 bg-cyan-500 rounded-full animate-bounce" style="animation-delay: 0.15s"></div>
              <div class="w-3 h-3 bg-cyan-500 rounded-full animate-bounce" style="animation-delay: 0.3s"></div>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- MODAL DE RESULTADO -->
    <Teleport to="body">
      <div v-if="mostrarResultado" class="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-[9999]" @click.self="cerrarResultado">
        <div :class="[
          'bg-white dark:bg-gray-800 rounded-3xl shadow-2xl w-full max-w-md overflow-hidden animate-zoomIn border-4',
          ultimoResultado.permitido ? 'border-emerald-500' : 'border-red-500'
        ]">
          <div :class="[
            'p-6 text-center',
            ultimoResultado.permitido ? 'bg-gradient-to-r from-emerald-500 to-teal-500' : 'bg-gradient-to-r from-red-500 to-rose-500'
          ]">
            <div class="w-24 h-24 mx-auto mb-4 rounded-full bg-white/20 flex items-center justify-center">
              <Icon :icon="ultimoResultado.permitido ? 'mdi:check-circle' : 'mdi:close-circle'" class="text-white text-7xl" />
            </div>
            <h2 class="text-white text-2xl font-black">
              {{ ultimoResultado.permitido ? 'ACCESO PERMITIDO' : 'ACCESO DENEGADO' }}
            </h2>
            <div class="mt-2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-black/20 text-white text-xs font-bold uppercase tracking-wider">
              <Icon :icon="ultimoResultado.tipoMetodo === 'cedula' ? 'mdi:card-account-details' : 'mdi:fingerprint'" />
              <span>Acceso por {{ ultimoResultado.tipoMetodo === 'cedula' ? 'Cédula' : 'Huella' }}</span>
            </div>
          </div>

          <div class="p-6 text-center">
            <div v-if="ultimoResultado.fotoSocio || ultimoResultado.fotoPerfilUsuario" class="w-24 h-24 mx-auto mb-3 rounded-full overflow-hidden border-4 border-gray-200 dark:border-gray-700 shadow-md">
              <img 
                :src="getFotoUrl(ultimoResultado.fotoSocio || ultimoResultado.fotoPerfilUsuario)" 
                class="w-full h-full object-cover"
                @error="(e) => e.target.style.display = 'none'"
              />
            </div>

            <p class="text-2xl font-black text-gray-800 dark:text-white mb-1">
              {{ ultimoResultado.nombreSocio || 'Desconocido' }}
            </p>
            <p v-if="ultimoResultado.documento" class="text-sm text-gray-500 font-mono mb-4">
              {{ ultimoResultado.documento }}
            </p>

            <div v-if="ultimoResultado.permitido" class="space-y-2 mt-4 bg-gray-50 dark:bg-gray-700/40 p-4 rounded-2xl">
              <div class="flex justify-between text-sm">
                <span class="text-gray-500 dark:text-gray-400">Membresía:</span>
                <span class="font-bold text-gray-800 dark:text-white">{{ ultimoResultado.tipoMembresia || 'Activa' }}</span>
              </div>
              <div class="flex justify-between text-sm">
                <span class="text-gray-500 dark:text-gray-400">Días restantes:</span>
                <span :class="[
                  'font-black text-lg',
                  ultimoResultado.diasRestantes > 7 ? 'text-emerald-600 dark:text-emerald-400' :
                  ultimoResultado.diasRestantes > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-red-600'
                ]">{{ ultimoResultado.diasRestantes }} días</span>
              </div>
              <div class="text-[11px] text-emerald-600 dark:text-emerald-400 font-bold flex items-center justify-center gap-1 mt-1">
                <Icon icon="mdi:gate" />
                <span>Torniquete habilitado</span>
              </div>
            </div>

            <div v-else class="space-y-2.5 mt-4 bg-red-50 dark:bg-red-900/20 p-4 rounded-2xl border border-red-200 dark:border-red-800">
              <p class="text-sm text-red-600 dark:text-red-400 font-bold flex items-center justify-center gap-1.5">
                <Icon icon="mdi:alert-circle" class="text-lg" />
                <span>{{ ultimoResultado.mensaje }}</span>
              </p>
              <div v-if="ultimoResultado.tipoMembresia || ultimoResultado.fechaFin" class="pt-2 border-t border-red-200/60 dark:border-red-800/60 space-y-1.5 text-xs text-left">
                <div v-if="ultimoResultado.tipoMembresia" class="flex justify-between">
                  <span class="text-gray-600 dark:text-gray-400">Último Plan:</span>
                  <span class="font-bold text-gray-800 dark:text-gray-200">{{ ultimoResultado.tipoMembresia }}</span>
                </div>
                <div v-if="ultimoResultado.fechaFin" class="flex justify-between">
                  <span class="text-gray-600 dark:text-gray-400">Vencimiento:</span>
                  <span class="font-bold text-red-600 dark:text-red-400">{{ formatFecha(ultimoResultado.fechaFin) }}</span>
                </div>
              </div>
            </div>

            <button @click="cerrarResultado"
              :class="[
                'mt-6 w-full py-3 rounded-xl font-bold text-white shadow-lg cursor-pointer transition-all active:scale-98',
                ultimoResultado.permitido ? 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700' : 'bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700'
              ]">
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- MODAL DE CONFIGURACIÓN DEL AGENTE LOCAL -->
    <Teleport to="body">
      <div v-if="showConfigAgente" class="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-[9999]" @click.self="showConfigAgente = false">
        <div class="bg-white dark:bg-gray-800 rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden animate-zoomIn border border-gray-200 dark:border-gray-700">
          <div class="bg-gradient-to-r from-gray-800 to-gray-900 text-white p-5 flex items-center justify-between">
            <div class="flex items-center gap-2">
              <Icon icon="mdi:laptop" class="text-2xl text-cyan-400" />
              <h3 class="font-bold text-lg">Agente Local del Gimnasio</h3>
            </div>
            <button @click="showConfigAgente = false" class="p-1 rounded-lg hover:bg-white/10 text-gray-300">
              <Icon icon="mdi:close" class="text-xl" />
            </button>
          </div>

          <div class="p-6 space-y-4">
            <p class="text-xs text-gray-500 dark:text-gray-400">
              El agente local corre en la laptop física del gimnasio y se encarga del hardware (Lector FS88H USB y Torniquete por puerto COM).
            </p>

            <div>
              <label class="block text-xs font-black text-gray-700 dark:text-gray-300 mb-1 uppercase">
                URL del Agente Local (Tailscale / Red Local)
              </label>
              <input
                v-model="agenteUrlInput"
                type="text"
                placeholder="https://desktop-qagollm.tailb8d19f.ts.net o http://localhost:5000"
                class="w-full px-3 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-white font-mono text-xs focus:outline-none focus:border-cyan-500"
              />
            </div>

            <div>
              <label class="block text-xs font-black text-gray-700 dark:text-gray-300 mb-1 uppercase">
                Puerto COM del Torniquete
              </label>
              <div class="flex gap-2">
                <input
                  v-model="puertoComInput"
                  type="text"
                  placeholder="COM4"
                  class="w-32 px-3 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-white font-mono text-xs focus:outline-none focus:border-cyan-500"
                />
                <button
                  @click="cambiarPuertoCom"
                  class="px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 rounded-xl text-xs font-bold text-gray-700 dark:text-gray-200 transition-all"
                >
                  Cambiar Puerto
                </button>
              </div>
            </div>

            <div class="p-3 bg-gray-100 dark:bg-gray-700/50 rounded-xl text-xs space-y-1 font-mono">
              <div class="flex justify-between">
                <span class="text-gray-500">Estado Torniquete:</span>
                <span :class="torniqueteStatus.conectado ? 'text-emerald-500 font-bold' : 'text-red-500 font-bold'">
                  {{ torniqueteStatus.conectado ? 'CONECTADO (' + torniqueteStatus.puerto + ')' : 'DESCONECTADO' }}
                </span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-500">Estado Huella FS88H:</span>
                <span :class="biometricoStatus.conectado ? 'text-emerald-500 font-bold' : 'text-red-500 font-bold'">
                  {{ biometricoStatus.conectado ? 'CONECTADO' : 'DESCONECTADO' }}
                </span>
              </div>
            </div>

            <div class="flex gap-2 pt-2">
              <button
                @click="probarConexionAgente"
                :disabled="probandoConexion"
                class="flex-1 py-2.5 bg-cyan-600 hover:bg-cyan-700 text-white rounded-xl font-bold text-xs shadow transition-all flex items-center justify-center gap-2"
              >
                <Icon :icon="probandoConexion ? 'mdi:loading' : 'mdi:lan-check'" :class="{'animate-spin': probandoConexion}" />
                {{ probandoConexion ? 'Probando...' : 'Probar Conexión' }}
              </button>

              <button
                @click="guardarConfigAgente"
                class="flex-1 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold text-xs shadow transition-all"
              >
                Guardar URL
              </button>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Resumen -->
    <div class="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
      <div class="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
        <p class="text-[10px] font-black text-gray-400 uppercase">Accesos Hoy</p>
        <p class="text-2xl font-black text-gray-800 dark:text-white">{{ accesos.length }}</p>
      </div>
      <div class="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-emerald-200">
        <p class="text-[10px] font-black text-emerald-600 uppercase">Permitidos</p>
        <p class="text-2xl font-black text-emerald-600">{{ totalPermitidos }}</p>
      </div>
      <div class="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-red-200">
        <p class="text-[10px] font-black text-red-600 uppercase">Denegados</p>
        <p class="text-2xl font-black text-red-600">{{ totalDenegados }}</p>
      </div>
      <div class="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-cyan-200">
        <p class="text-[10px] font-black text-cyan-600 uppercase">Socios Únicos</p>
        <p class="text-2xl font-black text-cyan-600">{{ sociosUnicos }}</p>
      </div>
    </div>

    <!-- Tabla -->
    <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div class="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
        <h3 class="font-bold text-sm text-gray-700 dark:text-white flex items-center gap-2">
          <Icon icon="mdi:history" class="text-cyan-500" />
          Accesos de Hoy
          <span class="text-xs text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-full">{{ accesos.length }}</span>
        </h3>
        <div class="flex items-center gap-2 text-[10px] text-green-500 font-bold">
          <span class="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
          EN VIVO
        </div>
      </div>
      <div class="overflow-x-auto">
        <table class="w-full text-left">
          <thead>
            <tr class="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700">
              <th class="px-3 py-3 text-[10px] font-black text-gray-400 uppercase">Socio</th>
              <th class="px-3 py-3 text-[10px] font-black text-gray-400 uppercase hidden md:table-cell">Método</th>
              <th class="px-3 py-3 text-[10px] font-black text-gray-400 uppercase">Plan / Vencimiento</th>
              <th class="px-3 py-3 text-[10px] font-black text-gray-400 uppercase text-center">Resultado</th>
              <th class="px-3 py-3 text-[10px] font-black text-gray-400 uppercase text-right">Hora</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
            <tr v-if="cargando && accesos.length === 0">
              <td colspan="5" class="px-3 py-4 text-center">
                <div class="animate-pulse h-4 bg-gray-100 dark:bg-gray-700 rounded"></div>
              </td>
            </tr>
            <tr v-else-if="accesos.length === 0">
              <td colspan="5" class="px-3 py-12 text-center">
                <Icon icon="mdi:door-closed" class="text-5xl text-gray-300 mx-auto mb-4" />
                <p class="text-gray-500 font-bold">Esperando accesos...</p>
                <p class="text-xs text-gray-400 mt-1">Coloque el dedo en el lector o ingrese la cédula arriba para registrar un acceso</p>
              </td>
            </tr>
            <tr v-else v-for="a in accesos" :key="a.idAcceso"
              :class="['transition-all hover:bg-gray-50 dark:hover:bg-gray-700/30',
                a.resultado === 'permitido' ? 'border-l-4 border-l-emerald-500' : 'border-l-4 border-l-red-500']">
              <td class="px-3 py-3">
                <div class="flex items-center gap-2">
                  <div class="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 bg-gray-100 dark:bg-gray-700 relative">
                    <img v-if="a.foto_socio || a.fotoSocio" :src="getFotoUrl(a.foto_socio || a.fotoSocio)" class="w-full h-full object-cover" @error="(e) => e.target.style.display = 'none'" />
                    <img v-else-if="a.foto_perfil_usuario || a.fotoPerfilUsuario" :src="getFotoUrl(a.foto_perfil_usuario || a.fotoPerfilUsuario)" class="w-full h-full object-cover" @error="(e) => e.target.style.display = 'none'" />
                    <div v-else :class="['w-full h-full flex items-center justify-center text-lg font-black',
                        a.resultado === 'permitido' ? 'bg-emerald-100 text-emerald-600' : 'bg-red-100 text-red-600']">
                      <Icon :icon="a.resultado === 'permitido' ? 'mdi:account-check' : 'mdi:account-cancel'" />
                    </div>
                  </div>
                  <div>
                    <div class="font-bold text-sm text-gray-800 dark:text-white">{{ a.nombre_socio || 'Desconocido' }}</div>
                    <div class="text-[10px] text-gray-400 font-mono">{{ a.documento_socio || a.valor_ingresado || '-' }}</div>
                  </div>
                </div>
              </td>
              <td class="px-3 py-3 hidden md:table-cell">
                <span :class="[
                  'px-2 py-0.5 rounded-full text-[10px] font-bold uppercase inline-flex items-center gap-1',
                  a.tipo_metodo === 'cedula' ? 'bg-purple-50 dark:bg-purple-900/20 text-purple-600' : 'bg-blue-50 dark:bg-blue-900/20 text-blue-600'
                ]">
                  <Icon :icon="a.tipo_metodo === 'cedula' ? 'mdi:card-account-details' : 'mdi:fingerprint'" />
                  {{ a.tipo_metodo || 'huella' }}
                </span>
              </td>
              <td class="px-3 py-3">
                <div class="flex flex-col gap-0.5">
                  <div class="flex items-center gap-1 font-bold text-xs text-gray-800 dark:text-gray-200">
                    <Icon icon="mdi:card-bulleted-outline" class="text-cyan-500 text-sm flex-shrink-0" />
                    <span class="truncate max-w-[130px] sm:max-w-[190px]">{{ a.tipo_membresia || a.tipoMembresia || 'Sin plan activo' }}</span>
                  </div>
                  <div class="flex items-center gap-1.5 text-[11px]">
                    <span class="text-gray-500 dark:text-gray-400 font-mono">
                      {{ (a.fecha_fin || a.fechaFin) ? formatFecha(a.fecha_fin || a.fechaFin) : '-' }}
                    </span>
                    <span v-if="a.dias_restantes !== undefined && a.dias_restantes !== null" :class="[
                      'text-[9px] px-1.5 py-0.2 rounded font-extrabold',
                      a.dias_restantes > 7 ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' :
                      a.dias_restantes > 0 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                      'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                    ]">
                      {{ a.dias_restantes > 0 ? (a.dias_restantes + ' d') : (a.dias_restantes === 0 ? 'Hoy' : 'Vencido') }}
                    </span>
                  </div>
                </div>
              </td>
              <td class="px-3 py-3 text-center">
                <span :class="['px-2 py-0.5 rounded-full text-[10px] font-black uppercase',
                  a.resultado === 'permitido' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700']">
                  {{ a.resultado }}
                </span>
                <div v-if="a.motivo_denegacion" class="text-[9px] text-red-500 mt-1 truncate max-w-[140px] mx-auto">{{ a.motivo_denegacion }}</div>
              </td>
              <td class="px-3 py-3 text-right text-xs text-gray-600 font-mono">{{ formatHora(a.fecha_hora) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { Icon } from '@iconify/vue';
import { useToast } from 'vue-toastification';
import Swal from 'sweetalert2';
import * as signalR from '@microsoft/signalr';
import { obtenerAccesosHoy, verificarFSO701 } from '@/api/membresias';
import { 
  estadoTorniquete, 
  abrirTorniqueteManual as abrirTorniqueteAPI,
  reproducirAudioAgente, 
  validarAccesoPorCedula, 
  getAgenteUrl, 
  setAgenteUrl, 
  cambiarPuertoTorniquete 
} from '@/api/acceso';
import { apiFetch, getCurrentApiUrl } from '@/api/index.js';

// 🔥 IMPORTS DE AUDIO
import audioBienvenida from '@/assets/audio/bienvenida.wav';
import audioDebeEsperar from '@/assets/audio/debeEsperar.wav';
import audioDiaNoPermitido from '@/assets/audio/diaNOpermitido.wav';
import audioEdadNoPermitida from '@/assets/audio/edadNOpermitida.wav';
import audioEntradasDiarias from '@/assets/audio/entradasDiarias.wav';
import audioEntradasSemanales from '@/assets/audio/entradasSemanales.wav';
import audioExpulsado from '@/assets/audio/expulsado.wav';
import audioExtraVencido from '@/assets/audio/extraVencido.wav';
import audioFelizCumpleanos from '@/assets/audio/felizCumpleanos.wav';
import audioFueraHorario from '@/assets/audio/fueraHorario.wav';
import audioHuellaNoRegistrada from '@/assets/audio/huellaNoRegistrada.wav';
import audioHuellaTrabajador from '@/assets/audio/huellaTrabajador.wav';
import audioInactivo from '@/assets/audio/inactivo.wav';
import audioNoEncontro from '@/assets/audio/noEncontro.wav';
import audioPasesAgotados from '@/assets/audio/pasesAgotados.wav';
import audioRecienInscrito from '@/assets/audio/recienInscrito.wav';
import audioSoloConHuella from '@/assets/audio/soloConHuella.wav';
import audioTrabajadorSoloHuella from '@/assets/audio/trabajadorSoloHuella.wav';
import audioVencido from '@/assets/audio/vencido.wav';
import audioVuelvaIntentarlo from '@/assets/audio/vuelvaIntentarlo.wav';
import audioVuelvaPonerHuella from '@/assets/audio/vuelvaPonerHuella.wav';
import audioZonaNoPermitida from '@/assets/audio/zonaNopermitida.wav';

// 🔥 MAPA DE AUDIOS (audioKey → archivo)
const AUDIOS = {
  bienvenida: audioBienvenida,
  debeEsperar: audioDebeEsperar,
  diaNOPermitido: audioDiaNoPermitido,
  edadNOPermitida: audioEdadNoPermitida,
  entradasDiarias: audioEntradasDiarias,
  entradasSemanales: audioEntradasSemanales,
  expulsado: audioExpulsado,
  extraVencido: audioExtraVencido,
  feliz_cumpleanos: audioFelizCumpleanos,
  fueraHorario: audioFueraHorario,
  huellaNoRegistrada: audioHuellaNoRegistrada,
  huellaTrabajador: audioHuellaTrabajador,
  inactivo: audioInactivo,
  noEncontro: audioNoEncontro,
  pasesAgotados: audioPasesAgotados,
  recienInscrito: audioRecienInscrito,
  soloConHuella: audioSoloConHuella,
  trabajadorSoloHuella: audioTrabajadorSoloHuella,
  vencido: audioVencido,
  vuelvaIntentarlo: audioVuelvaIntentarlo,
  vuelvaPonerHuella: audioVuelvaPonerHuella,
  zonaNoPermitida: audioZonaNoPermitida
};

const toast = useToast();

const HUB_URL = `${getCurrentApiUrl()}/asistenciaHub`;

const accesos = ref([]);
const cargando = ref(false);
const sonidoActivo = ref(true);
const sonidoDesbloqueado = ref(localStorage.getItem('audio_desbloqueado') === 'true');
const fso701Status = ref({ conectado: false });
const ultimoAcceso = ref('--:--:--');

const torniqueteStatus = ref({ conectado: false, puerto: 'COM4' });
const biometricoStatus = ref({ conectado: false });
const verificandoTorniquete = ref(false);
const verificandoBiometrico = ref(false);
const abriendoTorniquete = ref(false);

const escuchaActiva = ref(false);
const huellasProcessadas = ref(0);
const mostrarResultado = ref(false);
const mostrarVerificando = ref(false);
const metodoVerificando = ref('huella'); // 'huella' | 'cedula'
const ultimoResultado = ref({ permitido: false, mensaje: '', nombreSocio: '', tipoMetodo: 'huella' });

// Estados para Acceso por Cédula
const cedulaInput = ref('');
const procesandoCedula = ref(false);
const inputCedulaRef = ref(null);

// Estados para configuración de agente
const showConfigAgente = ref(false);
const agenteUrlInput = ref(getAgenteUrl());
const puertoComInput = ref('COM4');
const probandoConexion = ref(false);

let audioActual = null;
let intervalRefresh = null;
let intervalStatus = null;
let intervalHardware = null;
let hubConnection = null;
let ultimoIdConocido = null;
let timeoutCierre = null;
let timeoutVerificando = null;

const totalPermitidos = computed(() => accesos.value.filter(a => a.resultado === 'permitido').length);
const totalDenegados = computed(() => accesos.value.filter(a => a.resultado !== 'permitido').length);
const sociosUnicos = computed(() => {
  const set = new Set(accesos.value.map(a => a.idCliente || a.idUsuario || a.valor_ingresado).filter(Boolean));
  return set.size;
});

const formatHora = (f) => {
  if (!f) return '-';
  try { return new Date(f).toLocaleTimeString('es-VE', { hour: '2-digit', minute: '2-digit', second: '2-digit' }); }
  catch { return f; }
};

const formatFecha = (f) => {
  if (!f) return '-';
  try {
    const d = new Date(f);
    if (isNaN(d.getTime())) return f;
    return d.toLocaleDateString('es-VE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return f;
  }
};

const getFotoUrl = (foto) => {
  if (!foto) return null;
  if (foto.startsWith('http://') || foto.startsWith('https://') || foto.startsWith('data:image/')) return foto;
  const baseUrl = getCurrentApiUrl().replace(/\/+$/, '');
  const cleanPath = foto.replace(/^\/+/, '');
  if (cleanPath.startsWith('fotos_socios/') || cleanPath.startsWith('uploads/') || cleanPath.startsWith('imagenes/')) {
    return `${baseUrl}/${cleanPath}`;
  }
  return `${baseUrl}/imagenes/${cleanPath}`;
};

const toggleSonido = () => {
  sonidoActivo.value = !sonidoActivo.value;
  localStorage.setItem('acceso_sonido_activo', String(sonidoActivo.value));
  if (!sonidoActivo.value && audioActual) {
    audioActual.pause();
    audioActual = null;
  }
};

// 🔥 DESBLOQUEAR AUDIO MANUALMENTE
const desbloquearAudioManual = async () => {
  try {
    const audio = new Audio(audioBienvenida);
    audio.volume = 0.3;
    await audio.play();
    audio.pause();
    audio.currentTime = 0;
    
    sonidoDesbloqueado.value = true;
    localStorage.setItem('audio_desbloqueado', 'true');
    toast.success('🔊 Sonido activado correctamente');
  } catch (e) {
    console.error('❌ Error desbloqueando audio:', e);
    toast.error('No se pudo activar el sonido. Haga clic en la pantalla o verifique el volumen.');
  }
};

// ==================== ACCESO POR CÉDULA MANUAL / SCANNER ====================
const validarCedulaManual = async () => {
  const ced = cedulaInput.value.trim();
  if (!ced) {
    toast.warning('Ingrese un número de cédula o documento');
    return;
  }

  procesandoCedula.value = true;
  metodoVerificando.value = 'cedula';
  mostrarVerificando.value = true;

  try {
    const res = await validarAccesoPorCedula(ced);
    console.log('✅ Resultado validación cédula:', res);

    const permitido = !!res?.permitido;
    const socioNombre = res?.nombreSocio || res?.nombre_socio || 'Socio';
    const docSocio = res?.documento || res?.documento_socio || ced;
    const audioKey = res?.audioKey || (permitido ? 'bienvenida' : 'huellaNoRegistrada');

    ultimoResultado.value = {
      permitido,
      mensaje: res?.mensaje || (permitido ? 'Acceso permitido' : 'Acceso denegado'),
      audioKey,
      nombreSocio: socioNombre,
      documento: docSocio,
      tipoMembresia: res?.tipoMembresia || res?.tipo_membresia || '',
      diasRestantes: res?.diasRestantes ?? res?.dias_restantes ?? 0,
      fotoSocio: res?.fotoSocio || res?.foto_socio || null,
      fotoPerfilUsuario: res?.fotoPerfilUsuario || null,
      tipoMetodo: 'cedula'
    };

    mostrarVerificando.value = false;
    mostrarResultado.value = true;

    // Reproducir audio
    reproducirSonidoPorKey(audioKey, permitido);

    // Limpiar siempre el campo de cédula inmediatamente para la siguiente lectura
    cedulaInput.value = '';

    if (permitido) {
      toast.success(`✅ Acceso permitido: ${socioNombre}`);

      // 🔥 ABRIR EL TORNIQUETE EN LA LAPTOP LOCAL DEL GYM (reproduce 'bienvenida' y activa relay)
      try {
        const resTorniquete = await abrirTorniqueteAPI(audioKey || 'bienvenida');
        if (resTorniquete.success) {
          toast.success('🚪 Torniquete abierto en el gym');
        } else {
          console.warn('⚠️ Respuesta de apertura de torniquete:', resTorniquete);
        }
      } catch (errTorniquete) {
        console.error('❌ Error enviando apertura a torniquete:', errTorniquete);
      }
    } else {
      toast.error(`❌ ${ultimoResultado.value.mensaje}`);
      // 🔥 Reproducir voz de error directamente en las cornetas de la laptop del gym
      reproducirAudioAgente(audioKey || 'huellaNoRegistrada').catch(() => {});
    }

    ultimoAcceso.value = formatHora(new Date());
    setTimeout(cargar, 1000);

    if (timeoutCierre) clearTimeout(timeoutCierre);
    timeoutCierre = setTimeout(() => {
      if (mostrarResultado.value) mostrarResultado.value = false;
      inputCedulaRef.value?.focus();
    }, 5000);

  } catch (err) {
    console.error('❌ Error validando por cédula:', err);
    mostrarVerificando.value = false;
    toast.error('Error al validar cédula: ' + (err.message || 'Error de red'));
  } finally {
    procesandoCedula.value = false;
  }
};

// ==================== CONFIGURACIÓN DEL AGENTE LOCAL ====================
const guardarConfigAgente = () => {
  setAgenteUrl(agenteUrlInput.value);
  toast.success('✅ URL del Agente Local guardada');
  showConfigAgente.value = false;
  verificarTorniquete();
  verificarBiometrico();
};

const probarConexionAgente = async () => {
  probandoConexion.value = true;
  setAgenteUrl(agenteUrlInput.value);
  try {
    const rTorniquete = await estadoTorniquete();
    const rHuella = await fetch(`${getAgenteUrl()}/estado-huella`, { signal: AbortSignal.timeout(6000) })
      .then(r => r.json())
      .catch(() => ({ conectado: false }));

    torniqueteStatus.value = {
      conectado: rTorniquete.data?.conectado ?? false,
      puerto: rTorniquete.data?.puerto ?? 'COM4'
    };
    puertoComInput.value = torniqueteStatus.value.puerto;
    biometricoStatus.value = { conectado: rHuella?.conectado ?? false };

    if (rTorniquete.success || rHuella?.conectado) {
      toast.success('✅ Conexión con el agente local establecida');
    } else {
      toast.warning('⚠️ Agente respondió pero dispositivos aparecen desconectados');
    }
  } catch (e) {
    toast.error('❌ No se pudo conectar con el agente en esa URL: ' + e.message);
  } finally {
    probandoConexion.value = false;
  }
};

const cambiarPuertoCom = async () => {
  const p = puertoComInput.value.trim().toUpperCase();
  if (!p) return;
  try {
    const res = await cambiarPuertoTorniquete(p);
    if (res.success) {
      toast.success(`Puerto cambiado a ${p}`);
      torniqueteStatus.value.puerto = p;
    } else {
      toast.error(res.mensaje || 'Error al cambiar puerto');
    }
  } catch (e) {
    toast.error('Error al cambiar puerto: ' + e.message);
  }
};

// ==================== SIGNALR ====================
const conectarSignalR = async () => {
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');

  hubConnection = new signalR.HubConnectionBuilder()
    .withUrl(HUB_URL, {
      accessTokenFactory: () => token || '',
      transport: signalR.HttpTransportType.WebSocket | signalR.HttpTransportType.LongPolling
    })
    .withAutomaticReconnect()
    .build();

  hubConnection.on('HuellaProcesando', (data) => {
    if (mostrarResultado.value) {
      mostrarResultado.value = false;
      if (timeoutCierre) clearTimeout(timeoutCierre);
    }
    metodoVerificando.value = 'huella';
    mostrarVerificando.value = true;
    if (timeoutVerificando) clearTimeout(timeoutVerificando);
    timeoutVerificando = setTimeout(() => {
      if (mostrarVerificando.value) mostrarVerificando.value = false;
    }, 20000);
  });

  hubConnection.on('HuellaGymResultado', async (data) => {
    console.log('📢 HuellaGymResultado RECIBIDO:', data);

    mostrarVerificando.value = false;
    if (timeoutVerificando) {
      clearTimeout(timeoutVerificando);
      timeoutVerificando = null;
    }

    if (data?.tipoMetodo !== 'cedula') {
      huellasProcessadas.value++;
    }

    const permitido = !!data?.permitido;
    const tipoMetodo = data?.tipoMetodo || 'huella';

    ultimoResultado.value = {
      permitido,
      mensaje: data?.mensaje || (permitido ? 'Acceso permitido' : 'Acceso denegado'),
      audioKey: data?.audioKey || (permitido ? 'bienvenida' : 'huellaNoRegistrada'),
      nombreSocio: data?.nombreSocio || data?.nombre_socio || 'Desconocido',
      documento: data?.documento || data?.documento_socio || '',
      tipoMembresia: data?.tipoMembresia || data?.tipo_membresia || '',
      diasRestantes: data?.diasRestantes ?? data?.dias_restantes ?? 0,
      fotoSocio: data?.fotoSocio || data?.foto_socio || null,
      fotoPerfilUsuario: data?.fotoPerfilUsuario || data?.foto_perfil_usuario || null,
      tipoMetodo
    };

    mostrarResultado.value = true;
    
    // Reproducir audio según el audioKey
    reproducirSonidoPorKey(data?.audioKey, permitido);
    
    ultimoAcceso.value = formatHora(new Date());
    setTimeout(cargar, 1000);

    if (timeoutCierre) clearTimeout(timeoutCierre);
    timeoutCierre = setTimeout(() => {
      if (mostrarResultado.value) mostrarResultado.value = false;
      inputCedulaRef.value?.focus();
    }, 5000);
  });

  hubConnection.on('EstadoLector', (data) => {
    biometricoStatus.value = { conectado: !!data?.conectado };
  });

  hubConnection.on('EscuchaGymEstado', (data) => {
    escuchaActiva.value = !!data?.activo;
  });

  try {
    await hubConnection.start();
    console.log('✅ SignalR conectado. ConnectionId:', hubConnection.connectionId);
  } catch (e) {
    console.error('❌ Error SignalR:', e);
  }
};

const cerrarResultado = () => {
  if (timeoutCierre) clearTimeout(timeoutCierre);
  mostrarResultado.value = false;
  inputCedulaRef.value?.focus();
};

// 🔥 REPRODUCIR AUDIO POR KEY
const reproducirSonidoPorKey = (audioKey, permitido = true) => {
  if (!sonidoActivo.value) {
    console.log('🔇 Sonido desactivado');
    return;
  }

  try {
    if (audioActual) {
      audioActual.pause();
      audioActual.currentTime = 0;
    }

    let src = AUDIOS[audioKey];
    if (!src) {
      console.warn(`⚠️ AudioKey "${audioKey}" no encontrado, usando fallback`);
      src = permitido ? AUDIOS.bienvenida : AUDIOS.huellaNoRegistrada;
    }

    if (!src) return;

    audioActual = new Audio(src);
    audioActual.volume = 1.0;

    audioActual.play()
      .catch(err => {
        // Silenciar advertencia de navegador: el agente local en la laptop reproduce el audio por los altavoces
        const reproducirEnClic = () => {
          audioActual.play().catch(() => {});
          document.removeEventListener('click', reproducirEnClic);
          document.removeEventListener('touchstart', reproducirEnClic);
        };
        document.addEventListener('click', reproducirEnClic, { once: true });
        document.addEventListener('touchstart', reproducirEnClic, { once: true });
      });
  } catch (e) {
    console.error('💥 Error audio:', e);
  }
};

const reproducirSonido = (permitido) => {
  reproducirSonidoPorKey(permitido ? 'bienvenida' : 'huellaNoRegistrada', permitido);
};

const checkFSO701 = async () => {
  try {
    const res = await verificarFSO701();
    if (res.success && res.data) {
      fso701Status.value = res.data;
    } else {
      fso701Status.value = { conectado: false };
    }
  } catch (e) {
    fso701Status.value = { conectado: false };
  }
};

const verificarTorniquete = async () => {
  verificandoTorniquete.value = true;
  try {
    const res = await estadoTorniquete();
    if (res.success) {
      torniqueteStatus.value = {
        conectado: res.data?.conectado ?? res.conectado ?? false,
        puerto: res.data?.puerto ?? res.puerto ?? 'COM4'
      };
      puertoComInput.value = torniqueteStatus.value.puerto;
    } else {
      torniqueteStatus.value = { conectado: false, puerto: 'COM4' };
    }
  } catch (e) {
    torniqueteStatus.value = { conectado: false, puerto: 'COM4' };
  } finally {
    verificandoTorniquete.value = false;
  }
};

const verificarBiometrico = async () => {
  verificandoBiometrico.value = true;
  try {
    const response = await fetch(`${getAgenteUrl()}/estado-huella`, {
      method: 'GET',
      signal: AbortSignal.timeout(15000)
    });
    const data = await response.json();
    biometricoStatus.value = { conectado: data?.conectado ?? false };
  } catch (e) {
    biometricoStatus.value = { conectado: false };
  } finally {
    verificandoBiometrico.value = false;
  }
};

const activarEscuchaAgente = async () => {
  try {
    const response = await fetch(`${getAgenteUrl()}/activar-escucha`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({})
    });
    const data = await response.json();
    return data?.activo ?? data?.success ?? false;
  } catch (e) {
    return false;
  }
};

const desactivarEscuchaAgente = async () => {
  try {
    await fetch(`${getAgenteUrl()}/desactivar-escucha`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({})
    });
  } catch (e) {}
};

const confirmarAbrirTorniquete = async () => {
  const r = await Swal.fire({
    title: '¿Abrir torniquete?',
    text: 'Se enviará comando al puerto COM para abrir el torniquete.',
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: 'Sí, abrir',
    cancelButtonText: 'Cancelar',
    reverseButtons: true,
    customClass: {
      popup: 'rounded-2xl',
      confirmButton: 'swal-confirm-success',
      cancelButton: 'swal-cancel-btn'
    }
  });

  if (!r.isConfirmed) return;

  abriendoTorniquete.value = true;
  try {
    const res = await abrirTorniqueteAPI();
    if (res.success) {
      toast.success('✅ Torniquete abierto correctamente');
      setTimeout(cargar, 1000);
    } else {
      toast.error(res.error || res.mensaje || 'No se pudo abrir el torniquete');
    }
  } catch (e) {
    toast.error('Error al abrir el torniquete: ' + e.message);
  } finally {
    abriendoTorniquete.value = false;
  }
};

const cargar = async () => {
  cargando.value = true;
  try {
    const res = await obtenerAccesosHoy();
    if (res.success && res.data) {
      let lista = res.data;
      if (lista && !Array.isArray(lista) && Array.isArray(lista.data)) lista = lista.data;
      if (!Array.isArray(lista)) lista = [];

      if (lista.length > 0) {
        const ultimoId = lista[0].idAcceso;
        if (ultimoIdConocido !== null && ultimoId !== ultimoIdConocido) {
          const nuevoAcceso = lista[0];
          reproducirSonido(nuevoAcceso.resultado === 'permitido');
          ultimoAcceso.value = formatHora(nuevoAcceso.fecha_hora);
        } else if (ultimoIdConocido === null && lista.length > 0) {
          ultimoAcceso.value = formatHora(lista[0].fecha_hora);
        }
        ultimoIdConocido = ultimoId;
      }

      accesos.value = lista;
    }
  } catch (e) {
  } finally {
    cargando.value = false;
  }
};

onMounted(async () => {
  const guardado = localStorage.getItem('acceso_sonido_activo');
  if (guardado !== null) sonidoActivo.value = guardado === 'true';

  await conectarSignalR();
  await new Promise(r => setTimeout(r, 800));

  await verificarBiometrico();
  await verificarTorniquete();
  checkFSO701();
  await cargar();

  try {
    const activo = await activarEscuchaAgente();
    escuchaActiva.value = activo;
  } catch (e) {}

  intervalRefresh = setInterval(cargar, 6000);
  intervalStatus = setInterval(checkFSO701, 30000);
  intervalHardware = setInterval(() => {
    verificarTorniquete();
    verificarBiometrico();
  }, 15000);

  // Enfocar automáticamente el input de cédula para lectura rápida
  setTimeout(() => {
    inputCedulaRef.value?.focus();
  }, 500);
});

onUnmounted(async () => {
  if (intervalRefresh) clearInterval(intervalRefresh);
  if (intervalStatus) clearInterval(intervalStatus);
  if (intervalHardware) clearInterval(intervalHardware);
  if (timeoutCierre) clearTimeout(timeoutCierre);
  if (timeoutVerificando) clearTimeout(timeoutVerificando);

  await desactivarEscuchaAgente();

  if (hubConnection) await hubConnection.stop();
  if (audioActual) {
    audioActual.pause();
    audioActual = null;
  }
});
</script>

<style scoped>
.animate-fadeIn { animation: fadeIn 0.3s ease-out; }
.animate-zoomIn { animation: zoomIn 0.25s cubic-bezier(0.34, 1.56, 0.64, 1); }
@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
@keyframes zoomIn { from { opacity: 0; transform: scale(0.9); } to { opacity: 1; transform: scale(1); } }

@keyframes bounce-dot {
  0%, 80%, 100% { transform: translateY(0); }
  40% { transform: translateY(-10px); }
}
.animate-bounce { animation: bounce-dot 1.4s ease-in-out both; }

.swal-confirm-success {
  background: linear-gradient(135deg, #059669, #10b981) !important;
  color: white !important; padding: 0.65rem 1.5rem !important;
  border-radius: 0.75rem !important; font-weight: 700 !important;
  border: none !important; cursor: pointer !important;
}
.swal-cancel-btn {
  background: #f3f4f6 !important; color: #374151 !important;
  padding: 0.65rem 1.5rem !important;
  border-radius: 0.75rem !important;
  font-weight: 700 !important; border: 1px solid #e5e7eb !important;
  cursor: pointer !important;
}
</style>
